/**
 * BoostsService
 *
 * Domain layer service for the boost catalog. The catalog comes from the
 * `RemoteConfigs.boosts` Remote Config key — exactly Flutter's source
 * (`lib/services/remote_config_service.dart:84-108`) — and is remapped raw JSON
 * → DTO → entity here.
 *
 * The key is server-only (Flutter gives it no compile-time default,
 * `remote_config_service.dart:120-175`), so an absent, empty or malformed
 * template value yields `[]` via the four whole-payload guards
 * `getBoostsJson()` ports rather than throwing: the content monetization step
 * then renders no boost tiers instead of crashing.
 *
 * Per-tier malformation is guarded one level down, in
 * `BoostRemapper.fromDtoList`: a tier whose `options` are missing or not a list
 * is dropped from the catalog (Flutter throws on it and degrades the whole
 * catalog, `boost.g.dart:14-16` + `remote_config_service.dart:104-107`), so the
 * consumers' unguarded `options[0]` reads can never see an empty option list.
 *
 * Synchronous by design — `RemoteConfigService`'s getters are synchronous
 * (live value, else compile-time default), so `LoadBoostsUseCase.execute()` and
 * `useContentMonetization` keep reading the catalog without awaiting.
 */
import { BoostRemapper } from '@/domain/remappers/boost/boostRemapper';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import type { BoostEntity } from '@/domain/entities/boost/boostEntity';

export class BoostsService {
  private static instance: BoostsService;

  private constructor() { }

  public static getInstance(): BoostsService {
    if (!BoostsService.instance) {
      BoostsService.instance = new BoostsService();
    }
    return BoostsService.instance;
  }

  getBoosts(): BoostEntity[] {
    const dtos = BoostRemapper.fromJsonList(remoteConfigService.getBoostsJson());
    return BoostRemapper.fromDtoList(dtos);
  }
}

export const boostsService = BoostsService.getInstance();
